import isNil from 'lodash/isNil';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import fetchJson from '@atlassian/jira-fetch/src/utils/as-json.tsx';
import { fireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { PACKAGE_NAME } from '@atlassian/jira-servicedesk-getting-started-panel-common/src/constants.tsx';
import { withExponentialBackoffRetries } from '@atlassian/jira-servicedesk-getting-started-panel-common/src/utils/with-exponential-backoff-retries/index.tsx';
import type { ProjectId, BaseUrl, CloudId } from '@atlassian/jira-shared-types/src/general.tsx';
import {
	type GspState,
	defaultGspState,
	TaskVisibility,
} from '@atlassian/jsm-getting-started-panel';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import type { StoreActionApi } from '@atlassian/react-sweet-state';
import type { State, ActionProps } from '../../types.tsx';
import { handleOpsTeamMode } from '../handle-ops-team-mode/index.tsx';
import { handleUserCentricMode } from '../handle-user-centric-mode/index.tsx';
import { savePanelState } from '../save-panel-state/index.tsx';

export type InitializeState = (arg1: { projectId?: ProjectId }) => Promise<void>;

export const getUrl = ({ cloudId }: { baseUrl?: BaseUrl; cloudId: CloudId }): string =>
	`/jsm/onboarding/checklist/v1/cloudid/${cloudId}/aggregate/list/jsm-getting-started-panel`;

export const initializeState =
	(actionProps: ActionProps) =>
	({ setState, getState, dispatch }: StoreActionApi<State>): InitializeState =>
	async ({ projectId }) => {
		const {
			cloudId,
			isItsmSampleProject,
			createAnalyticsEvent,
			enableOpsTeamMode,
			teamId,
			enableUserCentricMode,
		} = actionProps;

		// Do not initialize unless it's known that project is not sample ITSM project
		if (isItsmSampleProject !== false) {
			if (isNil(isItsmSampleProject)) {
				fireErrorAnalytics({
					meta: {
						id: 'initializeState_isItsmSampleProject_unknown',
						packageName: PACKAGE_NAME,
						teamName: 'Sequoia',
					},
				});
			}
			return;
		}

		const initializeStateOnce = async (attemptIndex: number, totalAttempts: number) => {
			const isFinalAttempt = attemptIndex === totalAttempts - 1;

			// Stop any other updates to the state that might occur
			setState({ isSaving: true });

			// Fetch any existing data from ST (this will include any task visibility keys)
			let response;
			try {
				response = await fetchJson(getUrl({ cloudId }), {
					method: 'GET',
					mode: 'cors',
					credentials: 'include',
				});
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				// 404 indicates that the checklist is not yet initialized for the user
				if (error.statusCode === 404) {
					setState({
						isSaving: false,
						isGspInitialized: false,
					});
					return;
				}
				setState({ isSaving: false });
				if (isFinalAttempt) {
					fireErrorAnalytics({
						error,
						meta: {
							id: 'initializeState_fetch',
							packageName: PACKAGE_NAME,
							teamName: 'Sequoia',
						},
						sendToPrivacyUnsafeSplunk: true,
					});
				}
				throw error;
			}

			// TODO: validate properly https://jdog.jira-dev.com/browse/JIG-392
			// We can't use constructDataFromResponse because initial server data only
			// contains a subset of the full GSP state (just task visiblity keys)
			const serverData: GspState = response;

			/* Merge in server values and defaults, according to the following:
			 *  - completedItems from default state, to allow for reset behaviour with FSF override flag
			 *  - properties comes from server values first, to add task visibility and non-user properties
			 *  - default state user properties are merged in over the top of server properties to reset
			 *    things like sectionState, activeState, visualState, hasSeenReopenSpotlight in the
			 *    case of FSF override flag where server state already exists.
			 *    Assumption: the only properties contained in the default state are user properties
			 *  - projectId from current project
			 */
			let data: GspState = {
				completedItems: defaultGspState.completedItems,
				properties: {
					...serverData.properties,
					user: {
						...serverData.properties.user,
						...defaultGspState.properties.user,
						projectId,
					},
				},
			};

			if (enableUserCentricMode && data) {
				data = await handleUserCentricMode(data, cloudId);
			} else if (enableOpsTeamMode && teamId && data) {
				const opsAdminTaskVisibility =
					getState().data?.properties.user['ops-admin-tasks-visibility'] ?? TaskVisibility.Hidden;

				data.properties.user['ops-admin-tasks-visibility'] = opsAdminTaskVisibility;

				data = await handleOpsTeamMode(data, teamId, cloudId);
			}

			// Save our newly merged state
			try {
				setState({
					data,
					isGspInitialized: true,
					isSaving: false,
				});

				await dispatch(savePanelState(actionProps))(data);
				fireTrackAnalytics(createAnalyticsEvent({}), `${PACKAGE_NAME}.initializeState succeeded`);
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				setState({
					isGspInitialized: true,
					isSaving: false,
				});
				if (isFinalAttempt) {
					fireErrorAnalytics({
						error,
						meta: {
							id: 'initializeState',
							packageName: PACKAGE_NAME,
							teamName: 'Sequoia',
						},
						sendToPrivacyUnsafeSplunk: true,
					});
				}
			}
		};

		await withExponentialBackoffRetries({
			callback: initializeStateOnce,
			totalAttempts: 5,
			baseIntervalMs: 500,
		});
	};
